import { Box } from "@mui/material";
import { DotLegalDialog, DotLegalHeader, DotLegalSelect, DotLegalSwitch, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import { IGlobalSettingsDialogProps, useGlobalSettingsHook } from "./GlobalSettings.hooks";
import { useGlobalSettingsStyles } from "./GlobalSettings.styles";
import { RiskExposureMode } from "../../risk/riskExposure/RiskExposure.types";

function GlobalSettingsDialog(props: IGlobalSettingsDialogProps) {
    const styles = useGlobalSettingsStyles();
    const { translateString } = useTranslation();
    const {
        isLoading,
        value,
        onValueChange,
        onSave,
        saveEnabled,
        inProgress,
        risksOverAppetite,
        isExceeded,
        exposureMode,
        onExposureModeChange,
        exposureModeOptions,
        currencyCode,
        onCurrencyCodeChange,
        currencyOptions,
        scaleUpperBound,
        onScaleUpperBoundChange,
        exposureConfigChanged,
        showLegacyRiskAssessments,
        onShowLegacyRiskAssessmentsChange,
    } = useGlobalSettingsHook(props);

    return (
        <DotLegalDialog
            size="md"
            header={translateString("globalRiskSettings")}
            buttonOkText={translateString("save")}
            open
            isLoading={isLoading}
            okButtonDisabled={!saveEnabled}
            inProgress={inProgress}
            onDialogClose={() => props.onDialogClose()}
            onOkClick={() => onSave()}
            disableBackdropOnCloseIfValueChange={value}
        >
            <Box sx={styles.content}>
                <DotLegalSection>
                    <Box sx={styles.sectionContent}>
                        <DotLegalHeader center headerStyle="small">
                            {translateString("globalRiskAppetiteLimit")}
                        </DotLegalHeader>
                        <Box sx={styles.sectionDescription}>{translateString("maxRisksOverAppetiteDescription")}</Box>

                        <Box sx={styles.fieldRow}>
                            <Box sx={{ width: 45 }}>
                                <DotLegalTextField
                                    label=""
                                    value={value}
                                    onChange={onValueChange}
                                    debounce={false}
                                    onlyNumber="integer"
                                    minNumberValue={0}
                                    noMargin
                                />
                            </Box>
                            <Box sx={(theme) => ({ color: theme.palette.primary.dark, fontSize: theme.typography.pxToRem(14) })}>
                                {translateString("risksAboveLimit")}
                            </Box>
                        </Box>

                        {!isLoading && (
                            <Box
                                sx={(theme) =>
                                    isExceeded ? styles.currentTextExceeded : { color: theme.palette.primary.dark, fontSize: theme.typography.pxToRem(12) }
                                }
                            >
                                {translateString("currentRisksOverAppetite", { count: risksOverAppetite })}
                                {isExceeded && ` — ${translateString("appetiteExceeded")}`}
                            </Box>
                        )}
                    </Box>
                </DotLegalSection>

                <DotLegalSection>
                    <Box sx={styles.sectionContent}>
                        <DotLegalHeader center headerStyle="small">
                            {translateString("riskExposureSettingsHeader")}
                        </DotLegalHeader>
                        <Box sx={styles.sectionDescription}>{translateString("riskExposureSettingsDescription")}</Box>

                        <Box sx={styles.exposureFields}>
                            <DotLegalSelect
                                label={translateString("riskExposureMode")}
                                options={exposureModeOptions}
                                selectedItem={exposureMode.toString()}
                                onChange={onExposureModeChange}
                                disableClearable
                                noOptionsLabel={translateString("noOptions")}
                                noMargin
                            />

                            {exposureMode === RiskExposureMode.Scale && (
                                <DotLegalTextField
                                    noMargin
                                    label={translateString("riskExposureScaleUpperBound")}
                                    value={scaleUpperBound}
                                    onChange={onScaleUpperBoundChange}
                                    debounce={false}
                                    onlyNumber="integer"
                                    minNumberValue={2}
                                />
                            )}

                            {exposureMode !== RiskExposureMode.None && (
                                <DotLegalSelect
                                    noMargin
                                    label={translateString("currency")}
                                    options={currencyOptions}
                                    selectedItem={currencyCode}
                                    onChange={onCurrencyCodeChange}
                                    disableClearable
                                    noOptionsLabel={translateString("noOptions")}
                                    toolTipText={exposureMode === RiskExposureMode.Scale ? translateString("riskExposureCurrencyScaleHelpText") : undefined}
                                />
                            )}
                        </Box>

                        {exposureConfigChanged && <Box sx={styles.configChangeWarning}>{translateString("riskExposureConfigChangeWarning")}</Box>}
                    </Box>
                </DotLegalSection>

                <DotLegalSection>
                    <Box sx={styles.sectionContent}>
                        <DotLegalHeader center headerStyle="small">
                            {translateString("legacyRiskAssessmentsHeader")}
                        </DotLegalHeader>
                        <Box sx={styles.sectionDescription}>{translateString("legacyRiskAssessmentsDescription")}</Box>

                        <Box sx={styles.legacyToggleRow}>
                            <DotLegalSwitch
                                checked={showLegacyRiskAssessments}
                                rightLabel={translateString("showLegacyRiskAssessmentsInMenu")}
                                greyUncheckedTrack
                                onChange={onShowLegacyRiskAssessmentsChange}
                            />
                        </Box>

                        {/* DotLegalHeader renders color="secondary" as primary.main; it has no "main" value. */}
                        <DotLegalHeader center headerStyle="extraSmall" color="secondary" marginBottom={0}>
                            {showLegacyRiskAssessments ? translateString("legacyRiskAssessmentsVisible") : translateString("legacyRiskAssessmentsHidden")}
                        </DotLegalHeader>
                    </Box>
                </DotLegalSection>
            </Box>
        </DotLegalDialog>
    );
}

export default GlobalSettingsDialog;
